import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Cargo } from '../models/cargo.model';

const STORAGE_KEY = 'cargos';

/**
 * Lista de cargos e seus tempos padrão de turno (GET /bpv/cargos).
 * Cacheado em localStorage — atualizado no login (loadCargos) e, como
 * segurança, buscado uma vez sob demanda se o cache estiver vazio.
 */
@Injectable({ providedIn: 'root' })
export class CargoService {
  private readonly API_URL = environment.API_URL;
  private cargos: Cargo[] = this.carregarCache();

  constructor(private readonly http: HttpClient) {}

  /** Busca no backend e atualiza o cache. Chamado no login. */
  loadCargos(): Observable<Cargo[]> {
    return this.http.get<Cargo[]>(`${this.API_URL}/bpv/cargos`).pipe(
      tap(cargos => this.persistir(cargos)),
    );
  }

  /** Retorna o cache; se vazio, busca uma vez no backend. */
  ensureLoaded(): Observable<Cargo[]> {
    return this.cargos.length ? of(this.cargos) : this.loadCargos();
  }

  /**
   * Tempo padrão (min) do cargo, casando por `nomeIntegracao`.
   * Retorna null se o cargo não for encontrado ou o GET falhar — nesse caso
   * o chamador deve usar seu valor fixo de fallback.
   */
  getDefaultTimeMinutes(cargoNome: string | null | undefined): Observable<number | null> {
    return this.ensureLoaded().pipe(
      map(cargos => {
        if (!cargoNome) return null;
        const cargo = cargos.find(c => c.nomeIntegracao === cargoNome);
        return cargo ? cargo.defaultTimeMinutes : null;
      }),
      catchError(() => of(null)),
    );
  }

  private persistir(cargos: Cargo[]): void {
    this.cargos = cargos;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cargos));
  }

  private carregarCache(): Cargo[] {
    try {
      const bruto = localStorage.getItem(STORAGE_KEY);
      return bruto ? (JSON.parse(bruto) as Cargo[]) : [];
    } catch {
      return [];
    }
  }
}
